import { Dispatch, SetStateAction, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
	MRT_SortingState,
	MRT_VisibilityState,
	MRT_GroupingState,
	MRT_ColumnOrderState,
	MRT_ColumnFiltersState,
	MRT_ColumnFilterFnsState,
} from "material-react-table";

import type { SavedView } from "../../../../utilities/types";
import type { DashboardURLState } from "../JobsDashboardTable.types";
import {
	DEFAULT_FILTER_FNS,
	DEFAULT_FILTERS,
	DEFAULT_GROUPING,
	DEFAULT_COLUMN_SORT,
	DEFAULT_VIS_STATE,
	DEFAULT_COLUMN_ORDER,
} from "../JobsDashboardTable.types";
import { savedViewStateToDashboardState } from "../JobsDashboardTable.helpers";

export interface DashboardStateReturn {
	// MRT state
	sorting: MRT_SortingState;
	setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
	grouping: MRT_GroupingState;
	setGrouping: Dispatch<SetStateAction<MRT_GroupingState>>;
	columnOrder: MRT_ColumnOrderState;
	setColumnOrder: Dispatch<SetStateAction<MRT_ColumnOrderState>>;
	columnVisibility: MRT_VisibilityState;
	setColumnVisibility: Dispatch<SetStateAction<MRT_VisibilityState>>;
	columnFilters: MRT_ColumnFiltersState;
	setColumnFilters: Dispatch<SetStateAction<MRT_ColumnFiltersState>>;
	columnFilterFns: MRT_ColumnFilterFnsState;
	setColumnFilterFns: Dispatch<SetStateAction<MRT_ColumnFilterFnsState>>;

	// URL state
	searchParams: URLSearchParams;
	setSearchParams: ReturnType<typeof useSearchParams>[1];

	// Computed
	hasGrouping: boolean;
	selectedViewDirty: boolean;
	viewIdFromUrl: string | null;

	// Methods
	applyDashboardState: (state: DashboardURLState, branchCount: number) => void;
	applySavedViewState: (view: SavedView, branchCount: number) => void;
	syncUrlToState: (params: SyncUrlParams) => void;
	updateSelectedViewDirty: (selectedView: SavedView | null) => void;
}

export interface SyncUrlParams {
	selectedView: SavedView | null;
	isInitializing: boolean;
}

export function useDashboardState(): DashboardStateReturn {
	const [searchParams, setSearchParams] = useSearchParams();

	// Check if URL has ?v=<id> param (saved view format)
	const viewIdFromUrl = searchParams.get("v");

	const [sorting, setSorting] = useState<MRT_SortingState>(DEFAULT_COLUMN_SORT);
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(DEFAULT_VIS_STATE);
	const [grouping, setGrouping] = useState<MRT_GroupingState>(DEFAULT_GROUPING);
	const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(DEFAULT_COLUMN_ORDER);
	const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(DEFAULT_FILTERS);
	const [columnFilterFns, setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(DEFAULT_FILTER_FNS);

	const [selectedViewDirty, setSelectedViewDirty] = useState<boolean>(false);

	const hasGrouping = grouping.length > 0;

	function applyDashboardState(state: DashboardURLState, branchCount: number) {
		setSorting(state.sorting);
		let g = state.grouping;
		if (branchCount <= 1) {
			g = g.filter((group) => group !== "branch");
		}
		setGrouping(g);
		setColumnOrder(state.columnOrder);
		setColumnVisibility(state.columnVisibility);
		setColumnFilters(state.columnFilterValues);
		setColumnFilterFns({ ...DEFAULT_FILTER_FNS, ...state.columnFilterFunctions });
	}

	function applySavedViewState(view: SavedView, branchCount: number) {
		const dashState = savedViewStateToDashboardState(view.state);
		applyDashboardState(dashState, branchCount);
	}

	function syncUrlToState({ selectedView, isInitializing }: SyncUrlParams) {
		if (!selectedView) {
			if (isInitializing && searchParams.toString()) {
				return;
			}
			// Preserve the ?company= param (owned by CompanyContext)
			const companyParam = searchParams.get("company");
			const urlParams = new URLSearchParams();
			if (companyParam) {
				urlParams.set("company", companyParam);
			}
			setSearchParams(urlParams);
			return;
		}

		// Use short URL format: ?v=<viewId>, preserving ?company=
		const companyParam = searchParams.get("company");
		const urlParams = new URLSearchParams();
		urlParams.set("v", selectedView.id);
		if (companyParam) {
			urlParams.set("company", companyParam);
		}

		urlParams.sort();
		setSearchParams(urlParams);
	}

	function updateSelectedViewDirty(selectedView: SavedView | null) {
		if (selectedView === null) {
			setSelectedViewDirty(true);
			return;
		}

		// Compare current table state against the saved view's state
		const currentState = JSON.stringify({
			sorting,
			grouping,
			columnOrder,
			columnVisibility,
			columnFilterFunctions: columnFilterFns,
			columnFilterValues: columnFilters,
		});

		const viewState = JSON.stringify(selectedView.state);

		setSelectedViewDirty(currentState !== viewState);
	}

	return {
		sorting,
		setSorting,
		grouping,
		setGrouping,
		columnOrder,
		setColumnOrder,
		columnVisibility,
		setColumnVisibility,
		columnFilters,
		setColumnFilters,
		columnFilterFns,
		setColumnFilterFns,
		searchParams,
		setSearchParams,
		hasGrouping,
		selectedViewDirty,
		viewIdFromUrl,
		applyDashboardState,
		applySavedViewState,
		syncUrlToState,
		updateSelectedViewDirty,
	};
}
